import { FencyProvider } from "@fencyai/react";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useSub } from "../subscription/useSub";
import { AssistantPanel } from "./AssistantPanel";
import { AssistantPanelContext } from "./assistantPanelContext";
import { useAssistant } from "./useAssistant";

type AssistantContextValue = ReturnType<typeof useAssistant> & {
  draft: string;
  setDraft: (value: string) => void;
};

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export const useAssistantContext = (): AssistantContextValue => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error("useAssistantContext must be used within an AssistantProvider");
  }
  return context;
};

const ActiveAssistantProvider = ({ children }: { children: ReactNode }) => {
  const { fetchCreateStreamClientToken } = useFency();

  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <AssistantStateProvider>{children}</AssistantStateProvider>
    </FencyProvider>
  );
};

const AssistantStateProvider = ({ children }: { children: ReactNode }) => {
  const assistant = useAssistant();
  const [draft, setDraft] = useState("");
  const contextValue = useMemo(() => ({ ...assistant, draft, setDraft }), [assistant, draft]);

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
      <AssistantPanel />
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const { hasAssistantAccess } = useSub();
  const [panelOpen, setPanelOpen] = useState(false);
  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const panel = useMemo(() => ({ panelOpen, openPanel, closePanel }), [panelOpen, openPanel, closePanel]);

  return (
    <AssistantPanelContext.Provider value={panel}>
      {hasAssistantAccess ? <ActiveAssistantProvider>{children}</ActiveAssistantProvider> : <>{children}</>}
    </AssistantPanelContext.Provider>
  );
};
